export const cartLine = `
  id
  quantity
  attributes {
    key
    value
  }
  cost {
    totalAmount {
      amount
      currencyCode
    }
    subtotalAmount {
      amount
      currencyCode
    }
  }
  merchandise {
    ... on ProductVariant {
      id
      quantityAvailable
      price {
        amount
        currencyCode
      }
      compareAtPrice {
        amount
        currencyCode
      }
      image {
        src
        altText
        width
        height
      }
      product {
        ... on Product {
          id
          title
          handle
          sizeOption: metafield(namespace: "custom", key: "size") {
            value
          }
          burnedOption: metafield(namespace: "custom", key: "burn_rate") {
            value
          }
          widthOption: metafield(namespace: "custom", key: "weight") {
            value
          }
        }
      }
    }
  }
`;

export const cart = `
  checkoutUrl
  id
  totalQuantity
  cost {
    subtotalAmount {
      amount
      currencyCode
    }
    totalAmount{
      amount
      currencyCode
    }
  }
  lines(first: 50) {
    edges {
      node {
        ${cartLine}
      }
    }
  }
`;

export const variant = `
  availableForSale
  id
  barcode
  sku
  quantityAvailable
  title
  image {
    ... on Image{
      url
    }
  }
  product{
    ... on Product{
        id
      description
      handle
      title
      availableForSale
      tags
      sizeOption: metafield(namespace: "custom", key: "size") {
        value
      }
      burnedOption: metafield(namespace: "custom", key: "burn_rate") {
          value
      }
      shortDescription: metafield(
        namespace: "custom", key: "short_description"
      ) {
        value
      }
    }
  }
  price {
    amount
    currencyCode
  }
  compareAtPrice {
    amount
    currencyCode
  }
  selectedOptions {
    name
    value
  }
`;

const kitOptionProducts = `
  references(first: 30) {
    nodes {
      ... on Product {
        handle
        title
        id
        options {
          id
          name
          values
        }
        variants(first: 10) {
          edges {
            node {
              ${variant}
            }
          }
        }
      }
    }
  }
`;

export const product = `
  availableForSale
  handle
  id
  title
  handle
  tags
  description
  descriptionHtml
  vendor
  productType
  images(first: 50) {
    edges {
      node {
        alt: altText
        height
        id
        src: url(transform: { preferredContentType: WEBP })
        width
      }
    }
  }
  media(first: 50) {
    edges {
      node {
        alt
        mediaContentType
        ... on MediaImage {
          image {
            alt: altText
            height
            id
            src: url(transform: { preferredContentType: WEBP })
            width
          }
        }
        ... on Video {
          alt
          id
          sources {
            format
            height
            mimeType
            url
            width
          }
        }
      }
    }
  }
  options {
    id
    name
    values
  }
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  compareAtPriceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  variants(first: 100) {
    edges {
      node {
        ${variant}
      }
    }
  }
  excludePromo: metafield(namespace: "custom", key: "exclude_from_promotions") {
    value
    key
  }
  compareAtPriceBadge: metafield(
    namespace: "custom", 
    key: "show_compare_price_as_badge"
  ) {
    value
    key
  }
  sizeOption: metafield(namespace: "custom", key: "size") {
      value
  }
  burnedOption: metafield(namespace: "custom", key: "burn_rate") {
      value
  }
  shoppingGives: metafield(namespace: "custom", key: "shopping_gives"){
      value
  }
  tryBeforeYouBuy: metafield(
    namespace: "custom", key: "try_before_you_buy_reference"
  ){
    id
    reference
    {
      ... on Metaobject {
        handle
          id
          fields {
            type
            key
            value
            reference {
              ... on MediaImage {
                image {
                  originalSrc
                  altText
                }
              }
            }
          }
      }
    }
  }
  widthOption: metafield(namespace: "custom", key: "weight") {
      value
  }
  isGiftCard: metafield(namespace: "custom", key: "rise_ai_e_gift_card"){
      value
  }
  standardBadge: metafield(namespace: "custom", key: "badge") {
    id 
    reference
    {
      ... on Metaobject {
        handle
        id
        type
        fields{
          value
          key
        }
      }
    }
  }
  awardBadge: metafield(namespace: "custom", key: "award_badge") {
    id 
    reference
    {
      ... on Metaobject {
        handle
        id
        type
        fields{
          reference{
            ... on MediaImage{
              image {
                url
                altText
              }
            }
          }
        }
      }
    }
  }
  limitedTimeSubscribeSave: metafield(
    namespace: "custom", key: "limited_time_subscribe_save"
  ) {
    key
    value
  }
  promoMessage: metafield(namespace: "custom", key: "promo_meassage"){
    key
    value
  }
  descriptionIcons: metafield(
    namespace: "custom"
    key: "product_description_icons"
  ) {
    id
    references(first: 6) {
      edges {
        node {
          ... on Metaobject {
            handle
            id
            fields {
              value
              key
              type
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  scentColors: metafield(namespace: "custom", key: "scent_icons") {
    id
    references(first: 10) {
      edges {
        node {
          ... on Metaobject {
            handle
            id
            fields {
              value
              key
            }
          }
        }
      }
    }
  }
  detailVessel: metafield(namespace: "custom", key: "detail_vessel"){
    key
    value
  }
  detailWeight: metafield(namespace: "custom", key: "detail_weight"){
    key
    value
  }
  detailDimentions: metafield(namespace: "custom", key: "detail_dimensions"){
    key
    value
  }
  detailKeyIngredients: metafield(
    namespace: "custom",
    key: "detail_key_ingredients"
  ) {
    key
    value
  }
  detailItem: metafield(namespace: "custom", key: "detail_item"){
    key
    value
  }
  productAccordionTitle1: metafield(
    namespace: "custom", key: "product_accordion_1_title"
  ) {
    key
    value
  }
  productAccordionContent1: metafield(
    namespace: "custom", key: "product_accordion_1_content"
  ) {
    key
    value
  }
  productAccordionTitle2: metafield(
    namespace: "custom", key: "product_accordion_2_title"
  ) {
    key
    value
  }
  productAccordionContent2: metafield(
    namespace: "custom", key: "product_accordion_2_content"
  ) {
    key
    value
  }
  forcePdpATC: metafield(
    namespace: "custom", key: "force_pdp_atc"
  ) {
    key
    value
  }
  productAccordionTitle3: metafield(
    namespace: "custom", key: "product_accordion_3_title"
  ) {
    key
    value
  }
  productAccordionContent3: metafield(
    namespace: "custom", key: "product_accordion_3_content"
  ) {
    key
    value
  }
  disableBISNotification: metafield(
    namespace: "custom", key: "dsable_back_in_stock_notifications"
  ) {
    key
    value
  }
  shortDescription: metafield(namespace: "custom", key: "short_description") {
    value
  }
  upsellProduct: metafield(namespace: "custom", key: "upsell_product"){
    key
    value
    reference {
			... on ProductVariant {
        availableForSale
        id
        barcode
        sku
        quantityAvailable
        title
        image {
          ... on Image{
            url
          }
        }
        product{
            ... on Product{
            id
            description
            handle
            title
            availableForSale
            tags
            variants(first: 10) {
              edges {
                node {
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
            sizeOption: metafield(namespace: "custom", key: "size") {
              value
            }
            burnedOption: metafield(namespace: "custom", key: "burn_rate") {
              value
            }
            shortDescription: metafield(
              namespace: "custom", key: "short_description"
            ) {
              value
            }
          }
        }
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
        selectedOptions {
          name
          value
        }
      }
    }
  }
  productCardHoverImage: metafield(
    namespace: "custom", key: "product_card_hover_image"
  ){
    reference{
      ... on MediaImage{
        image {
          url
          altText
        }
      }
    }
  }
  kitOptionGroup1Label: metafield(
    namespace: "custom",
    key: "kit_option_group_1_label"
  ){
    value
  }
  kitOptionGroup1: metafield(
    namespace: "custom",
    key: "kit_option_group_1"
  ){
    key
    ${kitOptionProducts}
  }
  kitOptionGroup2Label: metafield(
    namespace: "custom",
    key: "kit_option_group_2_label"
  ){
    value
  }
  kitOptionGroup2: metafield(
    namespace: "custom",
    key: "kit_option_group_2"
  ){
    key
    ${kitOptionProducts}
  }
  kitOptionGroup3Label: metafield(
    namespace: "custom",
    key: "kit_option_group_3_label"
  ){
    value
  }
  kitOptionGroup3: metafield(
    namespace: "custom",
    key: "kit_option_group_3"
  ){
    key
    ${kitOptionProducts}
  }
  kitOptionGroup4Label: metafield(
    namespace: "custom",
    key: "kit_option_group_4_label"
  ){
    value
  }
  kitOptionGroup4: metafield(
    namespace: "custom",
    key: "kit_option_group_4"
  ){
    key
    ${kitOptionProducts}
  }
  kitOptionGroup5Label: metafield(
    namespace: "custom",
    key: "kit_option_group_5_label"
  ){
    value
  }
  kitOptionGroup5: metafield(
    namespace: "custom",
    key: "kit_option_group_5"
  ){
    key
    ${kitOptionProducts}
  }
  promoFlag: metafield(
    namespace: "custom",
    key: "promo_flag"
  ){
    key
    value
  }
  requiresSellingPlan
  sellingPlanGroups(first: 1) {
    edges {
      node {
        name
        options {
          name
          values
        }
        sellingPlans(first: 10) {
          edges {
            node {
              id
              name
              description
              recurringDeliveries
              priceAdjustments {
                orderCount
                adjustmentValue {
                  __typename
                  ... on SellingPlanPercentagePriceAdjustment {
                    adjustmentPercentage
                  }
                  ... on SellingPlanFixedAmountPriceAdjustment {
                    adjustmentAmount {
                      amount
                      currencyCode
                    }
                  }
                  ... on SellingPlanFixedPriceAdjustment {
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
